import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './header.css';
import MyFace from '../../assets/my-face.png';
import { useTheme } from '../../context/ThemeContext';

const NAV_ITEMS: Array<{ path: string; sectionId: string; label: string; icon: string }> = [
  { path: '/', sectionId: 'home', label: 'Home', icon: 'uil-estate' },
  { path: '/about', sectionId: 'about', label: 'About', icon: 'uil-user' },
  { path: '/skills', sectionId: 'skills', label: 'Skills', icon: 'uil-file-alt' },
  { path: '/experience', sectionId: 'experience', label: 'Experience', icon: 'uil-briefcase-alt' },
  { path: '/projects', sectionId: 'projects', label: 'Projects', icon: 'uil-scenery' },
  { path: '/blogs', sectionId: 'blogs', label: 'Blogs', icon: 'uil-notebooks' },
  { path: '/contact', sectionId: 'contact', label: 'Contact', icon: 'uil-message' },
];

const Header: React.FC = () => {
  const [toggle, setToggle] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<string>('home');
  const [showScrollHeader, setShowScrollHeader] = useState<boolean>(false);
  const { darkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    e.preventDefault();
    setToggle(false);
    if (pathname !== path) {
      navigate(path);
    } else {
      // Same route — just scroll to the section
      const sectionId = NAV_ITEMS.find(item => item.path === path)?.sectionId;
      if (sectionId) {
        const target = document.getElementById(sectionId);
        if (target) {
          const headerEl = document.querySelector('.header') as HTMLElement | null;
          const headerHeight = headerEl ? headerEl.offsetHeight : 0;
          window.scrollTo({ top: target.offsetTop - headerHeight, behavior: 'smooth' });
        }
      }
    }
  };

  const debounce = (func: Function, wait: number) => {
    let timeout: ReturnType<typeof setTimeout>;
    return function executedFunction(...args: any[]) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const handleScroll = useCallback(() => {
    const sections = document.querySelectorAll('section[id]');
    const scrollY = window.scrollY;

    if (scrollY >= 80) {
      setShowScrollHeader(true);
    } else {
      setShowScrollHeader(false);
    }

    let maxVisibleSection: string | null = null;
    let maxVisibleHeight = 0;

    sections.forEach(section => {
      const htmlSection = section as HTMLElement;
      const sectionHeight = htmlSection.offsetHeight;
      const sectionTop = htmlSection.offsetTop - 70;
      const sectionId = section.getAttribute('id') || '';

      const viewportHeight = window.innerHeight;
      const sectionBottom = sectionTop + sectionHeight;
      const visibleTop = Math.max(sectionTop, scrollY);
      const visibleBottom = Math.min(sectionBottom, scrollY + viewportHeight);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      if (visibleHeight > maxVisibleHeight) {
        maxVisibleHeight = visibleHeight;
        maxVisibleSection = sectionId;
      }
    });

    if (maxVisibleSection) {
      setActiveSection(maxVisibleSection);
    }
  }, []);

  useEffect(() => {
    const debouncedHandleScroll = debounce(handleScroll, 10);

    window.addEventListener('scroll', debouncedHandleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, [handleScroll]);

  return (
    <header className={showScrollHeader ? "header scroll-header" : "header"}>
      <nav className="nav container">
        <a href="/" className="nav__logo" onClick={(e) => handleNavClick(e, '/')}>
          <img src={MyFace} alt="My Face" className="nav__logo-img" />
          <span className="nav__logo-text">Adewale Adenle</span>
        </a>

        <div className={toggle ? "nav__menu show-menu" : "nav__menu"}>
          <ul className="nav__list grid">
            {NAV_ITEMS.map(({ path, sectionId, label, icon }) => (
              <li className="nav__item" key={path}>
                <a
                  href={path}
                  onClick={(e) => handleNavClick(e, path)}
                  className={activeSection === sectionId ? 'nav__link active-link' : 'nav__link'}
                >
                  <i className={`uil ${icon} nav__icon`}></i> {label}
                </a>
              </li>
            ))}
          </ul>

          <i className="uil uil-times nav__close" onClick={() => setToggle(!toggle)}></i>
        </div>

        <div className="nav__buttons">
          <button
            className="theme__toggle"
            onClick={toggleTheme}
            title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            <i className={`uil ${darkMode ? 'uil-sun' : 'uil-moon'}`}></i>
          </button>

          <div className="nav__toggle" onClick={() => setToggle(!toggle)}>
            <i className="uil uil-apps"></i>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
